import {
  AdgroupConfig,
  AdgroupFormData,
  AppEvents,
  BidStrategyType,
  CampaignFormData,
  SmartBidType,
  VOStatusConfig,
  VOStatus,
  AppProfilePageType,
  StrategyListItem,
  AdgroupListItem,
  CreateMode,
  ACTION_TYPE,
  DetailBidType,
} from '@tt4b/ttam-enums';
import {
  useAdgroupAppEvents,
  useAdgroupIos14,
  useAllowlist,
  useStdAdgroupCbo,
  useStdAdgroupIs,
  useStdAdgroupOptimizationGoal,
  useStdAdgroupPricing,
  useStdObjectiveType,
  useBidCapToCostCapStore,
  useAdgroupMode,
  useStdAdgroupBidStrategyIs,
} from '@tt4b/ttam-hooks';
import { Ref, computed, ComputedRef, ref } from '@vue/composition-api';
import { allStrategies } from '../consts';

/** Radio */

/**
 * 根据 strategy 类型筛选出对应 strategy 的配置项
 * @param types 当前可用的 strategy 类型
 */
const filterEnabledStrategies = (
  types: BidStrategyType[],
  campaignData: Ref<CampaignFormData>,
  isBidCapToCostCapTip: boolean,
) => allStrategies(campaignData, isBidCapToCostCapTip).filter(v => types.includes(v.type));

// eslint-disable-next-line max-lines-per-function
export function useRadiosPropsOptions(props: {
  voStatus: Ref<VOStatusConfig>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  appEvents: Ref<AppEvents>;
  dcAdgroupList: Ref<AdgroupListItem[]>;
  createMode?: Ref<CreateMode>;
  isBidCapToCostCapTip?: ComputedRef<boolean>;
}) {
  const {
    campaignData,
    adgroupData,
    appEvents,
    voStatus,
    isBidCapToCostCapTip,
    createMode = ref(ACTION_TYPE.CREATE) as Ref<CreateMode>,
  } = props;

  const { isAppInstall, isTraffic, isConversion } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const { isMaxCVNoBidWhite, isVoPangleInstallRoasWhite, isAeoDcCostCapWhite, isCBOCostCapWhite } = useAllowlist();

  const {
    isSPC,
    isOnlyPangle,
    isWebsite,
    isAppDownload,
    isStandardAdsWithCatalog,
    isStandardAdsWithShop,
    isStandardAdsWithShowcase,
    isLiveAds,
    isCLA,
    isPSA,
  } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isNonSpcCBO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isIOS14AppEvent } = useAdgroupIos14({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCPM, isCPX, isOCPX, isOCPC } = useStdAdgroupPricing({
    adgroupFormData: adgroupData,
  });
  const { isDeepExternalAction, isCurExternalActionSupportInAppEvent } = useAdgroupAppEvents({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
    appEvents,
  });
  const {
    isOptGoalClick,
    isOptGoalInstall,
    isOptGoalInAppEvent,
    isOptGoalFans,
    isOptGoalValue,
    isOptGoalConversion,
    isOptGoalLead,
  } = useStdAdgroupOptimizationGoal({
    adgroupFormData: adgroupData,
  });
  const { isHasTikTok } = useStdAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isEdit } = useAdgroupMode({ createMode });
  const { isCreateNonFirstCBOAdgroup, firstCboAdgroupData } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const isVOCostCapSupport = computed(() => voStatus.value.costCapStatus === VOStatus.QUALIFIED);
  const isVOMinRoasSupport = computed(() => voStatus.value.roasStatus === VOStatus.QUALIFIED);
  const isVOHighestValueSupport = computed(() => voStatus.value.highestValueStatus === VOStatus.QUALIFIED);
  const isVORoasSupport = computed(() => isVOMinRoasSupport.value || isVOHighestValueSupport.value);

  /** productsale shop 选中value */
  const isStandarAdsWithShopSelectVO = computed(() => isStandardAdsWithShop.value && isOptGoalValue.value);
  const isStandarAdsWithShowcaseSelectVO = computed(() => isStandardAdsWithShowcase.value && isOptGoalValue.value);
  const isPsaSelectVO = computed(() => isPSA.value && isOptGoalValue.value);

  /** productsale live 选中value */
  const isLiveAdsSelectVO = computed(() => isLiveAds.value && isOptGoalValue.value);

  /** APPINSTALL 支持Min Roas */
  const isAppinstallSupportRoasBid = computed(
    () =>
      isAppInstall.value &&
      ((isVORoasSupport.value && !isOnlyPangle.value) || (isOnlyPangle.value && isVoPangleInstallRoasWhite.value)),
  );
  /** Conversion APP 支持Min Roas */
  const isConversionAppSupportRoasBid = computed(
    () => isConversion.value && isAppDownload.value && isVORoasSupport.value,
  );
  /** Conversion WEB 支持Min Roas */
  const isConversionWebSupportRoasBid = computed(() => isConversion.value && isWebsite.value && isVORoasSupport.value);

  /** ProductSales Catalog app支持 Min Roas */
  const isStandardAdsWithCatalogAppSupportRoasBid = computed(
    () => isStandardAdsWithCatalog.value && isAppDownload.value && isVORoasSupport.value,
  );

  /** ProductSales Catalog web支持 Min Roas */
  const isStandardAdsWithCatalogWebSupportRoasBid = computed(
    () => isStandardAdsWithCatalog.value && isWebsite.value && isVORoasSupport.value,
  );
  /** ProductSales shop value支持 Min Roas */
  const isStandarAdsWithShopSupportRoasBid = computed(
    () => isStandarAdsWithShopSelectVO.value && isVORoasSupport.value,
  );
  const isStandarAdsWithShowcaseSupportRoasBid = computed(
    () => isStandarAdsWithShowcaseSelectVO.value && isVORoasSupport.value,
  );
  const isLiveAdsSupportRoasBid = computed(() => isLiveAdsSelectVO.value && isVORoasSupport.value);
  const isPsaSupportRoasBid = computed(() => isPsaSelectVO.value && isVORoasSupport.value);

  const isAppProfilePageTypeOn = computed(() => adgroupData.value?.adAppProfilePageType === AppProfilePageType.on);

  /**
   * 根据 formData 数据生成可用的 bid strategy 配置项
   * @param formData AdGroup FromData 数据
   * @param batchFormData AdGroup batchFormData 数据，主要用于获取 Split Test 信息
   */

  /** cbo只支持lowest cost */
  /** 判断是否包含 Bid Cap */
  const hasBidCap = () => {
    if (isSPC.value) {
      return false;
    }
    if (
      isStandarAdsWithShopSelectVO.value ||
      isStandarAdsWithShowcaseSelectVO.value ||
      isLiveAdsSelectVO.value ||
      isPsaSelectVO.value
    ) {
      return false;
    }

    return isCPX.value;
  };

  const notSupportCostCap = () => {
    if (isSPC.value) {
      return true;
    }
    if (isNonSpcCBO.value && !isCBOCostCapWhite.value) {
      return true;
    }
    if (isIOS14AppEvent.value && (isAppProfilePageTypeOn.value || !isAeoDcCostCapWhite.value || !isHasTikTok.value)) {
      return true;
    }
    return false;
  };

  /** 判断是否包含 Cost Cap */
  const hasCostCap = () => {
    if (notSupportCostCap()) {
      return false;
    }

    // VO需出价白名单支持
    if (isOptGoalValue.value) {
      // 下线VO CC & 支持存量回显
      return isEdit.value && adgroupData.value.smartBidType === SmartBidType.STANDARD;
    }

    // oCPC也支持cost-cap（CLA场景）
    return isOCPX.value || (isCLA.value && isOCPC.value);
  };

  const hasLowestCostForCPX = () => {
    // Traffic 和 App Install 下的 CPM 不支持 no bid
    if ((isTraffic.value || isAppInstall.value) && isCPM.value) {
      return false;
    }
    // 直投pangle也全量 cpc
    if (isOptGoalClick.value) {
      return true;
    }

    // CPX 的 no bid 已经全量
    return true;
  };

  const hasLowestCostForOCPX = () => {
    // Optimization Goal 为 VALUE(111) 不支持 no bid
    if (isOptGoalValue.value) {
      return false;
    }

    // 深度转化不支持 no bid
    if (isDeepExternalAction.value) {
      return false;
    }
    // conversion 不区分版位全量支持 lowest cost
    if (isOptGoalConversion.value) {
      return true;
    }
    if (isOptGoalInstall.value && isAppInstall.value) {
      return true;
    }

    // Optimization Goal 为 IN_APP_EVENT(106) 时，如果 is_no_bid_enabled 为 true 才支持 nobid（白名单豁免）
    if (isOptGoalInAppEvent.value) {
      return isCurExternalActionSupportInAppEvent.value;
    }

    // Optimization Goal 为 FANS(108) 和  INSTALL(105) LEAD(107) 支持 no bid（白名单豁免）
    if (isOptGoalInstall.value || isOptGoalFans.value || isOptGoalLead.value) {
      return true;
    }

    // MaxCV 如果在 max_conversion_no_bid(56) 中会支持 no bid
    return isMaxCVNoBidWhite.value;
  };

  /** 判断是否包含 Lowest Cap */
  const hasLowestCost = () => {
    // CLA不支持noBid
    if (isCLA.value) {
      return false;
    }

    if (isSPC.value) {
      return campaignData.value.smartBidType === SmartBidType.NO_BID;
    }

    if (isCPX.value) {
      return hasLowestCostForCPX();
    }

    if (isOCPX.value) {
      return hasLowestCostForOCPX();
    }

    // 其他情况兜底
    return false;
  };

  /** 判断是否包含 Max CV */
  const hasMaxCV = () =>
    // 如果之前包含 max cv，则保留
    adgroupData.value?.smartBidType === SmartBidType.MAX_CONVERTION;
  /** 判断是否包含 Min ROAS */
  const hasMinROAS = () => {
    // 只有VALUE支持
    if (!isOptGoalValue.value) {
      return false;
    }

    return (
      isAppinstallSupportRoasBid.value ||
      isConversionAppSupportRoasBid.value ||
      isConversionWebSupportRoasBid.value ||
      isStandardAdsWithCatalogAppSupportRoasBid.value ||
      isStandardAdsWithCatalogWebSupportRoasBid.value ||
      isStandarAdsWithShopSupportRoasBid.value ||
      isStandarAdsWithShowcaseSupportRoasBid.value ||
      isLiveAdsSupportRoasBid.value ||
      isPsaSupportRoasBid.value
    );
  };

  function generateStrategyList() {
    const list: BidStrategyType[] = [];

    if (hasBidCap()) {
      list.push(BidStrategyType.BID_CAP);
    }
    if (hasCostCap()) {
      list.push(BidStrategyType.COST_CAP);
    }
    if (hasLowestCost()) {
      list.push(BidStrategyType.LOWEST_COST);
    }
    if (hasMaxCV()) {
      list.push(BidStrategyType.MAXCV);
    }
    if (hasMinROAS()) {
      list.push(BidStrategyType.HIGHEST_VALUE);
    }

    const availableStrategies = filterEnabledStrategies(list, campaignData, isBidCapToCostCapTip?.value);
    // cbo下创建非第一个 adgroup，要选中第一个 ad 的 bid strategy
    if (isCreateNonFirstCBOAdgroup.value) {
      return availableStrategies.filter(item => item.value === (firstCboAdgroupData.value?.smartBidType as any));
    }

    return availableStrategies;
  }

  const strategyList: ComputedRef<StrategyListItem[]> = computed(() => generateStrategyList() as any);

  return {
    allOptions: allStrategies(campaignData, isBidCapToCostCapTip?.value),
    options: strategyList,
  };
}

export function useRadiosPropsDisabled(props: { adgroupConfig: Ref<AdgroupConfig> }) {
  return {
    disabled: computed(() => props?.adgroupConfig?.value?.disableBidStrategy),
  };
}

/** Set  */

export function useSetPropsVisible(props: {
  voStatus: Ref<VOStatusConfig>;
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { voStatus, adgroupData, campaignData } = props;
  const { isCBOVO } = useStdAdgroupCbo({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const isVOMinRoasSupport = computed(() => voStatus.value.roasStatus === VOStatus.QUALIFIED);
  const visible = (item: StrategyListItem) => {
    if (isCBOVO.value) {
      return adgroupData.value.roasStatus;
    }
    // Highest value 需要支持 Minimum ROAS 才展示输入
    // 否则可以根据配置，Cost Cap、Bid Cap 就展示
    return item.type === BidStrategyType.HIGHEST_VALUE ? isVOMinRoasSupport.value : item.hasBidSet;
  };
  return {
    visible,
  };
}

export function useSetPropsDisabled(props: { adgroupData: Ref<AdgroupFormData>; campaignData: Ref<CampaignFormData> }) {
  const { isCBOVO } = useStdAdgroupCbo({
    adgroupFormData: props.adgroupData,
    campaignFormData: props.campaignData,
  });

  return {
    disabled: computed(() => isCBOVO.value),
  };
}

export function useBidCapToCostCapEnable(props: {
  campaignData: Ref<CampaignFormData>;
  originAdgroupData: Ref<AdgroupFormData>;
  createMode: Ref<CreateMode>;
}) {
  const { originAdgroupData, createMode, campaignData } = props;
  const { isBidCapToCostCap } = useBidCapToCostCapStore({ campaignFormData: campaignData });
  const { isEdit } = useAdgroupMode({ createMode });
  const { isBidCap } = useStdAdgroupBidStrategyIs({ adgroupFormData: originAdgroupData });
  const isBidCapToCostCapEnable = computed(() => {
    // 非编辑根据是否开白判断
    if (!isEdit.value) {
      return isBidCapToCostCap.value;
    }
    // 编辑对存量数据不升级
    const isBidCapNotUpgraded = isBidCap.value && originAdgroupData.value.bidTypeDetail !== DetailBidType.UPGRADE;
    return isBidCapToCostCap.value && !isBidCapNotUpgraded;
  });

  return {
    isBidCapToCostCapEnable,
    isEdit,
  };
}

export function useBidCapToCostCapTipVisible(props: {
  isBidCapToCostCapEnable: ComputedRef<boolean>;
  isControlGroup: Ref<boolean>;
  adgroupData: Ref<AdgroupFormData>;
}) {
  const { isBidCapToCostCapEnable, isControlGroup, adgroupData } = props;
  const isBidCapToCostCapTipVisible = computed(() => {
    const isSplitTestLevelShowUpgrade =
      !isControlGroup.value || adgroupData.value.bidTypeDetail === DetailBidType.UPGRADE;
    return isBidCapToCostCapEnable.value && isSplitTestLevelShowUpgrade;
  });

  return {
    isBidCapToCostCapTipVisible,
  };
}
